class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.predictiveSearchResults.addEventListener('click', this.onPredictiveSearchClick.bind(this));
  }

  onPredictiveSearchClick(event) {
    const clickedResult = event.target.closest('a.predictive-search__item, button.predictive-search__item');
    if (!clickedResult || !this.predictiveSearchResults.contains(clickedResult)) return;

    let resultType = 'other';
    const href = clickedResult.getAttribute('href') || '';

    if (clickedResult.closest('#predictive-search-results-products-list')) {
      resultType = 'product';
    } else if (clickedResult.closest('#predictive-search-results-collections-list')) {
      resultType = 'collection';
    } else if (clickedResult.closest('#predictive-search-results-queries-list')) {
      resultType = 'suggestion';
    } else if (
      clickedResult.closest('#predictive-search-results-pages-list-mobile') ||
      clickedResult.closest('#predictive-search-results-pages-list-desktop')
    ) {
      resultType = href.includes('/blogs/') ? 'article' : 'page';
    } else if (clickedResult.closest('#predictive-search-option-search-keywords')) {
      resultType = 'all_results';
    }

    const listItem = clickedResult.closest('li');
    let resultPosition = null;

    if (listItem && listItem.parentElement) {
      const visibleSiblings = Array.from(listItem.parentElement.children).filter((item) => item.offsetParent !== null);
      const index = visibleSiblings.indexOf(listItem);
      if (index >= 0) resultPosition = index + 1;
    }

    const titleElement = clickedResult.querySelector('.predictive-search__item-heading');
    const resultTitle = (titleElement?.textContent || clickedResult.textContent || '').replace(/\s+/g, ' ').trim();
    const searchTerm = this.getQuery();
    const resultUrl = href || `${routes.search_url}?q=${encodeURIComponent(searchTerm)}`;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'predictive_search_click',
      search_term: searchTerm,
      result_type: resultType,
      result_position: resultPosition,
      result_title: resultTitle,
      result_url: resultUrl,
      search_source: 'header_predictive_search',
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=query,product,collection,page,article&resources[limit]=3&resources[limit_scope]=each&resources[options][fields]=title`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        
        // Filter out configurator products
        const filteredMarkup = this.filterConfiguratorProducts(resultsMarkup);
        
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = filteredMarkup;
        });
        this.renderSearchResults(filteredMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  // NEW METHOD: Filter out products with 'configurator' template suffix or vendor
  filterConfiguratorProducts(markup) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = markup;
    
    // Find all product list items in the search results
    const productListItems = tempDiv.querySelectorAll('#predictive-search-results-products-list li.predictive-search__list-item');
    
    productListItems.forEach(listItem => {
      const productLink = listItem.querySelector('a.predictive-search__item');
      
      if (productLink) {
        // Check the product URL
        const url = productLink.getAttribute('href') || '';
        
        // Check for vendor in the predictive-search__item-vendor element
        const vendorElement = listItem.querySelector('.predictive-search__item-vendor');
        const vendorText = vendorElement ? vendorElement.textContent.trim().toLowerCase() : '';
        
        // Remove items with configurator template suffix OR vendor
        const isConfigurator = url.includes('template_suffix=configurator') || vendorText === 'configurator';
        
        if (isConfigurator) {
          listItem.remove();
        }
      }
    });
    
    // Update the result count if it exists
    const countElement = tempDiv.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) {
      const remainingProducts = tempDiv.querySelectorAll('[data-product-url], .predictive-search__item[href*="/products/"]').length;
      // Update the count text to reflect filtered results
      const countText = countElement.textContent;
      const newCountText = countText.replace(/\d+/, remainingProducts);
      countElement.textContent = newCountText;
    }
    
    return tempDiv.innerHTML;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch); 